import { useTranslation } from "react-i18next";
import { money } from "../../lib/format";
import type { Quote } from "../../lib/types";

interface Props {
  quote: Quote | null;
  deposit: number;
  loading?: boolean;
}

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`flex justify-between text-xs ${strong ? "text-base font-black text-white" : "font-bold text-gray-300"}`}>
    <span>{label}</span>
    <span className="num font-mono">{value}</span>
  </div>
);

export function PriceBreakdown({ quote, deposit, loading }: Props) {
  const { t } = useTranslation();
  const cur = t("currency");
  if (!quote) return <div className="rounded-2xl border border-[#23285C] bg-[#161A42] p-4 text-xs font-bold text-gray-400">{loading ? t("common.loading") : t("booking.pickPackage")}</div>;
  const remaining = Math.max(0, quote.total - deposit);
  return (
    <div className="space-y-2 rounded-2xl border border-[#23285C] bg-[#161A42] p-4 text-white" aria-label={t("booking.breakdown")}>
      <Row label={t("booking.basePackage")} value={`${money(quote.base)} ${cur}`} />
      {quote.extraKidsCount > 0 && <Row label={t("booking.extraKids", { count: quote.extraKidsCount })} value={`${money(quote.extraKids)} ${cur}`} />}
      {quote.addons > 0 && <Row label={t("booking.addons")} value={`+${money(quote.addons)} ${cur}`} />}
      <Row label={t("booking.tax", { rate: quote.taxRateBp / 100 })} value={`${money(quote.tax)} ${cur}`} />
      <div className="my-1 border-t border-[#2B326B]" />
      <Row strong label={t("booking.grandTotal")} value={`${money(quote.total)} ${cur}`} />
      <Row label={t("booking.deposit")} value={`-${money(deposit)} ${cur}`} />
      <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2 text-xs font-black text-solar-gold">
        <span>{t("booking.remaining")}</span>
        <span className="num font-mono text-sm">{money(remaining)} {cur}</span>
      </div>
    </div>
  );
}
